import { DivConst } from 'constants/divConst';
import { useApiState } from 'hooks/useApiState';
import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useNavigate } from 'react-router-dom';
import type { AcctStateType } from 'stores/atoms/acctState';
import type { VoiceChatReportRefResponseType } from 'types/api/voiceChatReportRefResponseType';
import type { VoiceChatRespondentItemType } from 'types/api/voiceChatRespondentRefResponseType';
import type { VoiceChatViewerItemType } from 'types/api/voiceChatViewerRefResponseType';
import { getDefaultReportDateRange } from 'pages/ws/utils/reportDateRange';
import { openProductionUrl } from 'utils/voiceChat';
import { downloadCsvFromBase64 } from 'utils/downloadCsv';

export interface UseWsReportVoiceChatResult {
  initialized: boolean;
  processing: boolean;
  report: VoiceChatReportRefResponseType | null;
  respondents: VoiceChatRespondentItemType[];
  viewers: VoiceChatViewerItemType[];
  viewerFilterDiv: number;
  onChangeViewerFilter: (div: number) => void;
  onDownloadViewerCsv: (encoding?: 'utf8' | 'shift_jis' | null) => Promise<void>;
  reportDateFrom: string;
  reportDateTo: string;
  isExpiredPlan: boolean;
  modalTermOpen: boolean;
  setModalTermOpen: (open: boolean) => void;
  modalDownloadOpen: boolean;
  setModalDownloadOpen: (open: boolean) => void;
  onContentOpen: () => void;
  onModalReportDownloadOpen: () => void;
  onModalReportTermOpen: () => void;
  onChangeReportTerm: (reportDateFrom: string, reportDateTo: string) => void;
}

/**
 * ボイスチャットレポート画面のカスタムフック（useWsReportForm ミラー）。
 * postVoiceChatReportRef（日次集計・設問要約）+ postVoiceChatRespondentRef（回答者一覧）を統合する。
 */
export function useWsReportVoiceChat(
  voiceChatCd: string | undefined,
  company: NonNullable<AcctStateType['company']>
): UseWsReportVoiceChatResult {
  const {
    postVoiceChatReportRef,
    postVoiceChatRespondentRef,
    postVoiceChatViewerRef,
    postVoiceChatViewerDownload,
  } = useApiState();
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();

  const [initialized, setInitialized] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [report, setReport] = useState<VoiceChatReportRefResponseType | null>(null);
  const [respondents, setRespondents] = useState<VoiceChatRespondentItemType[]>([]);
  const [viewers, setViewers] = useState<VoiceChatViewerItemType[]>([]);
  const [viewerFilterDiv, setViewerFilterDiv] = useState(0);
  const [reportDateFrom, setReportDateFrom] = useState('');
  const [reportDateTo, setReportDateTo] = useState('');
  const [modalTermOpen, setModalTermOpen] = useState(false);
  const [modalDownloadOpen, setModalDownloadOpen] = useState(false);

  const isExpiredPlan = company.plan.isExpired;

  /**
   * 視聴者一覧を取得してセットする（page size 100）。
   */
  const _fetchViewers = async (from: string, to: string, filterDiv: number) => {
    const res = await postVoiceChatViewerRef({
      voiceChatCd: voiceChatCd ?? '',
      reportDateFrom: from,
      reportDateTo: to,
      viewerStatusFilterDiv: filterDiv,
      limit: 100,
      offset: 0,
    });
    setViewers(res.viewers);
  };

  const initState = async () => {
    try {
      const { reportDateFrom: from, reportDateTo: to } = getDefaultReportDateRange(new Date());

      const [reportResponse, respondentResponse] = await Promise.all([
        postVoiceChatReportRef({
          voiceChatCd,
          reportDateFrom: from,
          reportDateTo: to,
        }),
        postVoiceChatRespondentRef({
          voiceChatCd,
          limit: 100,
          offset: 0,
        }),
        _fetchViewers(from, to, 0),
      ]);

      setReportDateFrom(from);
      setReportDateTo(to);
      setReport(reportResponse);
      setRespondents(respondentResponse.respondents);
    } catch (e: unknown) {
      showBoundary(e);
    } finally {
      setInitialized(true);
    }
  };

  useEffect(() => {
    // initState は useCallback 化されていないため render ごとに参照が変わる。
    // マウント時の 1 回だけ実行するため意図的に空配列を使用する。
    // eslint-disable-next-line react-hooks/set-state-in-effect -- 初回マウント時の状態初期化のため意図的
    initState();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時 1 回のみ実行する初期化処理（initState は useCallback 化されていないため）
  }, []);

  /**
   * コンテンツ確認ボタン押下時の処理（useWsReportForm ミラー）。
   * 公開中は公開URLを開き、それ以外は編集画面へ遷移する。
   */
  const onContentOpen = () => {
    if (report?.voiceChatStatusDiv === DivConst.items.voiceChatStatus.published.value) {
      openProductionUrl(report.cd);
    } else {
      navigate(`/ws/${company.cd}/voice_chat/edit/${voiceChatCd}`);
    }
  };

  /**
   * レポート出力ボタン押下時の処理
   */
  const onModalReportDownloadOpen = () => {
    if (isExpiredPlan) {
      return;
    }
    setModalDownloadOpen(true);
  };

  /**
   * 集計期間変更ボタン押下時の処理
   */
  const onModalReportTermOpen = () => {
    setModalTermOpen(true);
  };

  /**
   * 集計期間変更モーダルで期間を変更した場合の処理
   */
  const onChangeReportTerm = async (from: string, to: string) => {
    try {
      setProcessing(true);

      const response = await postVoiceChatReportRef({
        voiceChatCd,
        reportDateFrom: from,
        reportDateTo: to,
      });

      setReportDateFrom(from);
      setReportDateTo(to);
      setReport(response);
      await _fetchViewers(from, to, viewerFilterDiv);
    } catch (e: unknown) {
      showBoundary(e);
    } finally {
      setProcessing(false);
    }
  };

  /**
   * 視聴者フィルタ変更時の処理（EV-vc-viewer-01）。
   * ハンドラ内で直接 _fetchViewers を呼ぶ（初回マウントとの二重発火を避けるため useEffect 方式は使わない）。
   */
  const onChangeViewerFilter = async (div: number) => {
    setViewerFilterDiv(div);
    try {
      await _fetchViewers(reportDateFrom, reportDateTo, div);
    } catch (e: unknown) {
      showBoundary(e);
    }
  };

  /**
   * CSV ダウンロード処理（EV-vc-viewer-02）。
   */
  const onDownloadViewerCsv = async (encoding?: 'utf8' | 'shift_jis' | null) => {
    try {
      const res = await postVoiceChatViewerDownload({
        voiceChatCd: voiceChatCd ?? '',
        reportDateFrom,
        reportDateTo,
        viewerStatusFilterDiv: viewerFilterDiv,
        encoding,
      });
      downloadCsvFromBase64(res.fileName, res.fileData);
    } catch (e: unknown) {
      showBoundary(e);
    }
  };

  return {
    initialized,
    processing,
    report,
    respondents,
    viewers,
    viewerFilterDiv,
    onChangeViewerFilter,
    onDownloadViewerCsv,
    reportDateFrom,
    reportDateTo,
    isExpiredPlan,
    modalTermOpen,
    setModalTermOpen,
    modalDownloadOpen,
    setModalDownloadOpen,
    onContentOpen,
    onModalReportDownloadOpen,
    onModalReportTermOpen,
    onChangeReportTerm,
  };
}
